import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { Toast, ToastMessage } from "primereact/toast";

/**
 * Единая точка показа уведомлений.
 *
 * До этого каждая страница заводила собственный `useRef<Toast>` и свой
 * `<Toast />` в разметке — 30 копий одного и того же. Здесь один экземпляр
 * на всё приложение, страницы получают доступ через `useNotify()`.
 *
 * Метод `show` намеренно повторяет сигнатуру PrimeReact ToastMessage,
 * чтобы перевод страниц свёлся к замене `toast.current?.show(` на
 * `toast.show(` без правки самих сообщений.
 */
export interface NotificationApi {
  show: (message: ToastMessage | ToastMessage[]) => void;
  clear: () => void;
  success: (summary: string, detail?: string) => void;
  error: (summary: string, detail?: string) => void;
  warn: (summary: string, detail?: string) => void;
  info: (summary: string, detail?: string) => void;
}

const NotificationContext = createContext<NotificationApi | undefined>(
  undefined,
);

const DEFAULT_LIFE = 3000;

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const toastRef = useRef<Toast>(null);

  const show = useCallback((message: ToastMessage | ToastMessage[]) => {
    toastRef.current?.show(message);
  }, []);

  const clear = useCallback(() => {
    toastRef.current?.clear();
  }, []);

  const api = useMemo<NotificationApi>(() => {
    const withSeverity =
      (severity: ToastMessage["severity"]) =>
      (summary: string, detail?: string) =>
        show({ severity, summary, detail, life: DEFAULT_LIFE });

    return {
      show,
      clear,
      success: withSeverity("success"),
      error: withSeverity("error"),
      warn: withSeverity("warn"),
      info: withSeverity("info"),
    };
  }, [show, clear]);

  return (
    <NotificationContext.Provider value={api}>
      <Toast ref={toastRef} />
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotify = (): NotificationApi => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotify должен вызываться внутри NotificationProvider");
  }
  return context;
};

export default NotificationContext;
